import { createApi } from "@reduxjs/toolkit/query/react"
import { baseQuery } from "../api"
import {
  AddThreadParams,
  GetCalendarIntegrationResponse,
  GetThreadMessagesParams,
  GetThreadMessagesResponse,
  GetThreadParams,
  GetThreadByIdParams,
  GetThreadsParams,
  Thread,
  ThreadBasic,
  ThreadReplyParams,
  Threads,
  UpdateThreadParams
} from "../../types"
import {
  DELETE_THREAD_ENDPOINT,
  GET_THREAD_ENDPOINT,
  NEW_THREAD_ENDPOINT,
  REPLY_THREAD_ENDPOINT,
  SMS_THREAD_CALENDAR_INTEGRATION,
  SMS_THREAD_MESSAGES,
  THREADS_ENDPOINT,
  UPDATE_THREAD_ENDPOINT
} from "../../common/constants"

const reducerPath = "conversationsApi"

const isTypeThreads = (
  response: Threads | { status: string; message: string }
): response is Threads => {
  return Array.isArray(response)
}

export const conversationsApi = createApi({
  reducerPath,
  baseQuery,
  endpoints: builder => ({
    getThreads: builder.query<Threads, GetThreadsParams>({
      query: params => {
        return {
          url: THREADS_ENDPOINT,
          method: "GET",
          params
        }
      },
      transformResponse: (
        response: Threads | { status: string; message: string }
      ) => {
        if (!isTypeThreads(response)) {
          console.error(response.message)
          return []
        } else {
          // Filter out threads where the messages array is empty
          const filteredThreads = response.filter(thread => {
            return !!thread.messages.length || thread.status === "scheduled"
          })
          // Order the filtered threads by the last message timestamp
          const orderedThreads = filteredThreads.sort((a, b) => {
            const lastMessageA = a.messages.length
              ? a.messages[a.messages.length - 1]
              : { timestamp: a.updatedAt }
            const lastMessageB = b.messages.length
              ? b.messages[b.messages.length - 1]
              : { timestamp: b.updatedAt }
            const timestampA = new Date(lastMessageA.timestamp).getTime()
            const timestampB = new Date(lastMessageB.timestamp).getTime()
            return timestampB - timestampA
          })
          return orderedThreads
        }
      }
    }),
    getThreadMessages: builder.query<
      GetThreadMessagesResponse,
      GetThreadMessagesParams
    >({
      query: ({ timestamp_min = 10000, brand_id, loid }) => ({
        url: SMS_THREAD_MESSAGES,
        method: "GET",
        params: { timestamp_min, brand_id, loid }
      }),
      transformResponse: (
        response: GetThreadMessagesResponse
      ): GetThreadMessagesResponse => {
        if (Array.isArray(response)) {
          return response
        } else {
          return []
        }
      }
    }),

    getThread: builder.query<Thread, GetThreadParams>({
      query: params => ({
        url: GET_THREAD_ENDPOINT,
        method: "GET",
        params
      })
    }),
    getThreadById: builder.query<Thread, GetThreadByIdParams>({
      query: params => ({
        url: GET_THREAD_ENDPOINT,
        method: "GET",
        params
      })
    }),
    addThread: builder.mutation<Thread, AddThreadParams>({
      query: newThread => ({
        url: NEW_THREAD_ENDPOINT,
        method: "POST",
        body: newThread
      })
    }),
    updateThread: builder.mutation<void, UpdateThreadParams>({
      query: updatedThread => ({
        url: UPDATE_THREAD_ENDPOINT,
        method: "POST",
        body: updatedThread
      })
    }),
    replyThread: builder.mutation<void, ThreadReplyParams>({
      query: reply => ({
        url: REPLY_THREAD_ENDPOINT,
        method: "POST",
        body: reply
      })
    }),
    deleteThread: builder.mutation<void, { threadId: string }>({
      query: ({ threadId }) => ({
        url: `${DELETE_THREAD_ENDPOINT}?thread_id=${threadId}`,
        method: "DELETE"
      })
    }),
    getCalendarIntegration: builder.query<
      GetCalendarIntegrationResponse | [],
      ThreadBasic
    >({
      query: params => ({
        url: SMS_THREAD_CALENDAR_INTEGRATION,
        method: "GET",
        params
      })
    })
  })
})

export const {
  useGetThreadsQuery,
  useLazyGetThreadsQuery,
  useGetThreadMessagesQuery,
  useGetThreadQuery,
  useLazyGetThreadQuery,
  useGetThreadByIdQuery,
  useAddThreadMutation,
  useUpdateThreadMutation,
  useDeleteThreadMutation,
  useReplyThreadMutation,
  useGetCalendarIntegrationQuery
} = conversationsApi
